import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { fetchChatHistoryByUser, projectCreated, projectRenamed, projectDeleted } from '../slice/api/historySlice';
import { fetchTasksAsync } from '../slice/taskSlice';
import { parseISO, format } from 'date-fns';
import { deleteHistory, updateHistoryName, createChatHistory, uploadFilesWithHistory } from '../Api/req';
import { fetchSQByTaskId } from '../slice/sqSlice';
import { getTaskStatusesByHistoryAsync } from '../slice/taskStatusSlice';
import { fetchAgentLayout } from '../slice/agentWidgetsSlice';
import AnswerStatusBadges from '../components/atomic/AnswerStatusBadges';
import { useThemeColor } from '../hooks/useThemeColor';
import { useTranslation } from '../i18n/LanguageContext';
import { v4 as uuidv4 } from 'uuid';
import toast, { Toaster } from 'react-hot-toast';
import * as pdfjsLib from 'pdfjs-dist';
import SharePointFileBrowser from './settings/components/SharePointFileBrowser';
import { clearSyncResults } from '../slice/sharepointSlice';
import { BASE_URL, BASE_SHARES_URL } from '../components/constants';
import axios from 'axios';
import CustomImageField from './settings/components/CustomImageField';
import CustomFileField from './settings/components/CustomFileField';
import { authHeader, authHeaders } from '../utils/authHeaders';
import {
  Search,
  Plus,
  MoreHorizontal,
  MoreVertical,
  LayoutGrid,
  List,
  Home,
  Eye,
  Pencil,
  Trash2,
  X,
  ChevronDown,
  Check,
  Upload,
  FolderOpen,
  FileText,
  Image as ImageIcon,
  AlertCircle,
  Share2
} from 'lucide-react';
import ShareModal from '../components/ShareModal';

// Ensure pdfjs worker is set up for counting pages
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Project Icon Component for List View — solid app theme color so every
// project bubble follows the active aispace color instead of the previous
// hardcoded yellow. Reads the theme via hook so no prop drilling needed.
const ProjectIcon = () => {
  const { aispaceColor } = useThemeColor();
  return (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: `${aispaceColor}20`, color: aispaceColor }}
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: aispaceColor }}>
        <path d="M12 4L4 8L12 12L20 8L12 4Z" fill="currentColor" fillOpacity="0.9"/>
        <path d="M4 12L12 16L20 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.9"/>
        <path d="M4 16L12 20L20 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.7"/>
      </svg>
    </div>
  );
};

// Project Card Banner for Grid View — solid app theme color, matching the
// list-view bubble so the banner doesn't fall back to the old blue gradient.
const ProjectBanner = () => {
  const { aispaceColor } = useThemeColor();
  return (
    <div
      className="w-full h-40 rounded-t-xl flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: `${aispaceColor}20` }}
    >
      <svg className="w-32 h-32 opacity-80" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: aispaceColor }}>
        <path d="M60 20L20 45L60 70L100 45L60 20Z" fill="currentColor" fillOpacity="0.9"/>
        <path d="M20 65L60 90L100 65" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M20 80L60 105L100 80" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.7"/>
      </svg>
    </div>
  );
};

// Project image component — matches AgentImage pattern. The fallback
// icon/banner pull theme color from useThemeColor() themselves, so no
// `themeColor` prop is needed at the call site.
const ProjectImage = ({ projectId, imagePath, type = 'icon' }) => {
  const [imgError, setImgError] = useState(false);
  const showImage = imagePath && !imgError;
  const imgSrc = imagePath ? `${BASE_URL}/projects/image/${projectId}` : '';

  if (type === 'banner') {
    return (
      <div className="w-full h-40">
        {showImage ? (
          <img src={imgSrc} alt="" className="w-full h-full object-cover" onError={() => setImgError(true)} />
        ) : (
          <ProjectBanner />
        )}
      </div>
    );
  }

  // icon type (list view)
  return showImage ? (
    <img src={imgSrc} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" onError={() => setImgError(true)} />
  ) : (
    <ProjectIcon />
  );
};

const History = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { aispaceColor } = useThemeColor();
  const { userHistories: chatHistory = [], userHistoriesStatus: status } = useSelector((state) => state.history);
  const { tasks = [] } = useSelector((state) => state.tasks);
  const { data: userData } = useSelector((state) => state.admin);
  const { syncResults } = useSelector((state) => state.sharepoint);
  const { showProjectDetailByTask, overviewEnabledByTask, layoutLoadingByTask, layoutByTask } = useSelector((state) => state.agentWidgets);
  const overviewDisabled = useSelector((state) => !!state.theme.organization?.overview_disabled);

  // Get agent filter from URL params
  const agentFilter = searchParams.get('agent');
  const agentNameFromUrl = searchParams.get('agentName');

  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('list');
  const [historyData, setHistoryData] = useState(new Map());
  const [openDropdown, setOpenDropdown] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, project: null, isBulk: false });
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [showHeaderDropdown, setShowHeaderDropdown] = useState(false);
  const [selectionModeActive, setSelectionModeActive] = useState(false);
  const [createProjectModal, setCreateProjectModal] = useState(false);
  const [showSharePointBrowser, setShowSharePointBrowser] = useState(false);
  const [shareTarget, setShareTarget] = useState(null); // { id, name }
  const [receivedShares, setReceivedShares] = useState([]); // projects shared with me
  const [sharedHistoryIds, setSharedHistoryIds] = useState(new Set()); // projects I shared
  const [projectsTab, setProjectsTab] = useState(() => searchParams.get('tab') === 'shared' ? 'shared' : 'my')
  // Filter modal state
  const [projFilterOpen, setProjFilterOpen] = useState(false);
  const [projFilterAgents, setProjFilterAgents] = useState([]); // selected agent ids
  const [projFilterDate, setProjFilterDate] = useState(null); // 'today'|'7days'|'30days'
  const [projFilterMonth, setProjFilterMonth] = useState('');
  const [projFilterProgress, setProjFilterProgress] = useState(null); // 'low'|'medium'|'high'
  const [projFilterSort, setProjFilterSort] = useState('newest');
  // Draft states for modal
  const [draftAgents, setDraftAgents] = useState([]);
  const [draftDate, setDraftDate] = useState(null);
  const [draftMonth, setDraftMonth] = useState('');
  const [draftProgress, setDraftProgress] = useState(null);
  const [draftSort, setDraftSort] = useState('newest');
  const [agentDdOpen, setAgentDdOpen] = useState(false);
  const [monthDdOpen, setMonthDdOpen] = useState(false);
  const [sortDdOpen, setSortDdOpen] = useState(false);
  const agentDdRef = useRef(null);
  const monthDdRef = useRef(null);
  const sortDdRef = useRef(null);

  const hasActiveFilter = projFilterAgents.length > 0 || projFilterDate || projFilterMonth || projFilterProgress;
  const [importedSharePointFiles, setImportedSharePointFiles] = useState([]);
  const [newProject, setNewProject] = useState({
    agent: '',
    projectName: '',
    projectPicture: null,
    uploadMethod: '',
    selectedFolders: [],
    uploadedFiles: [],
    selectedSharePointFiles: [],
    sharePointConfigured: false
  });
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [isStartingDirectChat, setIsStartingDirectChat] = useState(false);
  const [histAgentDropdownOpen, setHistAgentDropdownOpen] = useState(false);
  const histAgentDropdownRef = useRef(null);
  const histAgentTriggerRef = useRef(null);
  const [histAgentMenuPos, setHistAgentMenuPos] = useState(null);
  const dropdownRef = useRef(null);
  const headerDropdownRef = useRef(null);
  const projectPictureRef = useRef(null);
  const documentInputRef = useRef(null);

  // Per-agent custom Project Setup fields (defined in Agent Settings).
  const [customFields, setCustomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [customFieldErrors, setCustomFieldErrors] = useState({});
  const [customFieldsLoading, setCustomFieldsLoading] = useState(false);

  useEffect(() => {
    const onDocClick = (e) => {
      if (histAgentDropdownRef.current && !histAgentDropdownRef.current.contains(e.target)) setHistAgentDropdownOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  // Fetch custom Project Setup fields whenever the selected agent changes.
  useEffect(() => {
    if (!createProjectModal || !newProject.agent) {
      setCustomFields([]);
      setCustomFieldValues({});
      setCustomFieldErrors({});
      setCustomFieldsLoading(false);
      return;
    }
    let cancelled = false;
    // Clear stale custom fields from the previously selected agent so they
    // don't flash before the new schema arrives.
    setCustomFields([]);
    setCustomFieldValues({});
    setCustomFieldErrors({});
    setCustomFieldsLoading(true);
    axios
      .get(`${BASE_URL}/agent-custom-fields/task/${newProject.agent}`, authHeaders())
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.fields || [];
        setCustomFields(list);
        const initial = {};
        list.forEach((f) => {
          initial[f.id] = (f.field_type === 'image' || f.field_type === 'file')
            ? []
            : (f.default_value || '');
        });
        setCustomFieldValues(initial);
        setCustomFieldErrors({});
      })
      .catch(() => {
        if (cancelled) return;
        setCustomFields([]);
        setCustomFieldValues({});
        setCustomFieldErrors({});
      })
      .finally(() => { if (!cancelled) setCustomFieldsLoading(false); });
    return () => { cancelled = true; };
  }, [createProjectModal, newProject.agent])

  // Use different items per page for grid (divisible by 3 for complete rows) and list view
  const itemsPerPageGrid = 9; // 3 complete rows
  const itemsPerPageList = 10;
  const itemsPerPage = viewMode === 'grid' ? itemsPerPageGrid : itemsPerPageList;
  const user_id = localStorage.getItem('id');
  const email = localStorage.getItem('email');

  // Same role + department logic as HomeScreen.jsx
  const userRole = userData?.user_role ?? null;
  const isAdminRole  = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN';
  const isNormalUser = userRole === 'normal';
  const userDepartmentNames = Array.isArray(userData?.department_names)
    ? userData.department_names
    : [];

  // Build agent list for Create Project modal + Project Filter dropdown.
  // Rules (identical to HomeScreen):
  //   - userData not loaded       → [] (no flash)
  //   - ADMIN / SUPER_ADMIN       → all org agents
  //   - normal, has departments   → only agents whose department matches AND have a department set
  //   - normal, no departments    → [] (same as home page — no agents shown)
  //   - anything else (No License)→ []
  const _allAgents = tasks
    .filter((task) => !!task.department_name && task.name !== '__CHAT_INTERNAL__')
    .map((task, index) => ({
      id: task.id,
      name: task.name || t('analysis.agentFallback'),
      description: task.description || t('homepage.agentCardDefaultDescription'),
      department: task.department_name,
      category: task.category || task.type || 'General',
      highlighted: index % 2 === 1,
      direct_chat_mode: !!task.direct_chat_mode,
    }));

  // ITEM 1: "projects" > "filter" --> only show agents based on user department.
  // Role is checked first so ADMIN/SUPER_ADMIN always sees all agents.
  // Normal users: filter by department_names if available; otherwise fall back to
  // the backend-filtered _allAgents (backend already scopes by user_email).
  const agents = !userData
    ? []
    : isAdminRole
      ? _allAgents
      : isNormalUser
        ? (userDepartmentNames.length > 0
            ? _allAgents.filter((a) => userDepartmentNames.includes(a.department))
            : _allAgents)
        : [];

  // ADMIN 1: Create-project modal — admin/super-admin see ALL agents (no department filter).
  // Normal users keep the same department-scoped list.
  const agentsForModal = !userData
    ? []
    : isAdminRole
      ? tasks
          .filter((task) => task.name !== '__CHAT_INTERNAL__')
          .map((task, index) => ({
            id: task.id,
            name: task.name || t('analysis.agentFallback'),
            description: task.description || t('homepage.agentCardDefaultDescription'),
            department: task.department_name,
            category: task.category || task.type || 'General',
            highlighted: index % 2 === 1,
            direct_chat_mode: !!task.direct_chat_mode,
          }))
      : agents;

  // Whether any real (non-internal) agent exists — gates the Create Project button.
  const hasUsableAgents = agentsForModal.length > 0;

  // Fetch chat history — skip if already loaded (create/rename/delete keep
  // this in sync locally now, so a remount no longer needs a full refetch).
  useEffect(() => {
    if (user_id && status !== 'succeeded' && status !== 'loading') {
      dispatch(fetchChatHistoryByUser(user_id));
    }
  }, [dispatch, user_id, status]);

  // Fetch projects shared with me — re-fetch on every navigation to this page
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${BASE_SHARES_URL}/received`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { projects: [] })
      .then(d => setReceivedShares(d.projects || []))
      .catch(() => setReceivedShares([]));
  }, [location.key]);

  // Fetch history_ids I have shared (for Can Edit badge)
  const fetchMyShares = () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${BASE_SHARES_URL}/my-shares`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { history_ids: [] })
      .then(d => setSharedHistoryIds(new Set((d.history_ids || []).map(String))))
      .catch(() => {});
  };
  useEffect(() => { fetchMyShares(); }, []);

  // Fetch tasks for agents
  useEffect(() => {
    if (email) {
      dispatch(fetchTasksAsync(email));
    }
  }, [dispatch, email]);

  // Helper function to check if task_id is a valid numeric ID (not a UUID)
  const isValidNumericTaskId = (taskId) => {
    if (!taskId) return false;
    const taskIdStr = taskId.toString();
    // UUIDs contain hyphens, numeric IDs don't
    // Also check if it's actually a number
    return !taskIdStr.includes('-') && !isNaN(Number(taskIdStr));
  };

  // Fetch agent layouts for ALL task_ids so feature flags are populated
  // Guard: skip if already loading or already loaded for this task
  useEffect(() => {
    if (chatHistory?.length > 0) {
      const fetchedTaskIds = new Set();
      chatHistory.forEach(record => {
        if (!record.task_id) return;
        const tid = record.task_id.toString();
        if (fetchedTaskIds.has(tid)) return;
        if (layoutLoadingByTask[tid] || layoutByTask[tid]) return;
        fetchedTaskIds.add(tid);
        dispatch(fetchAgentLayout(tid));
      });
    }
  }, [chatHistory, dispatch, layoutLoadingByTask, layoutByTask]);

  // Fetch data for each history item
  useEffect(() => {
    if (chatHistory?.length > 0 || receivedShares?.length > 0) {
      setHistoryData(new Map());
    }

    if (chatHistory?.length > 0) {
      chatHistory.forEach(record => {
        if (record.task_id && record.id) {
          // Only fetch SQ data if task_id is a valid numeric ID (not a UUID)
          if (isValidNumericTaskId(record.task_id)) {
            dispatch(fetchSQByTaskId(record.task_id))
              .then((action) => {
                if (action.payload) {
                  setHistoryData(prev => {
                    const newMap = new Map(prev);
                    const currentData = newMap.get(record.id) || {};
                    newMap.set(record.id, {
                      ...currentData,
                      questions: action.payload.questions || []
                    });
                    return newMap;
                  });
                }
              });
          }

          dispatch(getTaskStatusesByHistoryAsync(record.id))
            .then((action) => {
              if (action.payload) {
                setHistoryData(prev => {
                  const newMap = new Map(prev);
                  const currentData = newMap.get(record.id) || {};
                  newMap.set(record.id, {
                    ...currentData,
                    statuses: action.payload || []
                  });
                  return newMap;
                });
              }
            });
        }
      });
    }

    // Fetch Q&A progress and statuses for invited/shared projects
    if (receivedShares?.length > 0) {
      receivedShares.forEach(share => {
        if (!share.history_id) return;

        if (isValidNumericTaskId(share.task_id)) {
          dispatch(fetchSQByTaskId(share.task_id))
            .then((action) => {
              if (action.payload) {
                setHistoryData(prev => {
                  const newMap = new Map(prev);
                  const currentData = newMap.get(share.history_id) || {};
                  newMap.set(share.history_id, {
                    ...currentData,
                    questions: action.payload.questions || []
                  });
                  return newMap;
                });
              }
            });
        }

        dispatch(getTaskStatusesByHistoryAsync(share.history_id))
          .then((action) => {
            if (action.payload) {
              setHistoryData(prev => {
                const newMap = new Map(prev);
                const currentData = newMap.get(share.history_id) || {};
                newMap.set(share.history_id, {
                  ...currentData,
                  statuses: action.payload || []
                });
                return newMap;
              });
            }
          });
      });
    }
  }, [chatHistory, receivedShares, dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
      if (headerDropdownRef.current && !headerDropdownRef.current.contains(event.target)) {
        setShowHeaderDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear selections and disable selection mode when page or view changes
  useEffect(() => {
    setSelectedProjects(new Set());
    setSelectionModeActive(false);
  }, [currentPage, viewMode]);

  const formatTimestamp = (timestamp) => {
    try {
      const date = parseISO(timestamp);
      return format(date, 'dd.MM.yyyy');
    } catch {
      return 'Ungültiges Datum';
    }
  };

  const getCompletedQuestions = (historyId, taskId = null) => {
    const taskIdStr = taskId?.toString();
    if (taskIdStr && showProjectDetailByTask[taskIdStr] === false) {
      return { completed: 0, total: 0 };
    }
    const data = historyData.get(historyId) || {};
    const questions = data.questions || [];
    const statuses = data.statuses || [];
    if (!questions.length) {
      return { completed: 0, total: 0 };
    }
    const validSqIds = new Set(questions.map(q => parseInt(q.id)));
    const completedCount = statuses.filter(
      ts => ts.status === true && validSqIds.has(parseInt(ts.sq_id))
    ).length;
    return { completed: completedCount, total: questions.length };
  };

  const getProjectStatuses = (historyId, taskId = null) => {
    const taskIdStr = taskId?.toString();
    if (taskIdStr && showProjectDetailByTask[taskIdStr] === false) return [];
    const data = historyData.get(historyId) || {};
    const questions = data.questions || [];
    const statuses = data.statuses || [];
    if (!questions.length) return [];
    const validSqIds = new Set(questions.map(q => parseInt(q.id)));
    return statuses.filter(ts => validSqIds.has(parseInt(ts.sq_id)));
  };

  // Debug: Log agent filter info
  useEffect(() => {
    if (agentFilter) {
      console.log('=== History Page Debug ===');
      console.log('Agent Filter:', agentFilter, 'Agent Name:', agentNameFromUrl);
      console.log('Total chatHistory items:', chatHistory.length);
      const matchingProjects = chatHistory.filter(item => item.task_id?.toString() === agentFilter);
      console.log('Matching projects for this agent:', matchingProjects.length);
      console.log('ChatHistory task_ids:', chatHistory.map(item => ({ task_id: item.task_id, name: item.history_name || item.task_name })));
    }
  }, [agentFilter, agentNameFromUrl, chatHistory]);

  // Helper: get Q&A completed count for a project
  const getQACompleted = (hid) => {
    const d = historyData.get(hid) || {};
    return (d.statuses || []).filter(s => s.status === true).length;
  };

  // Merge own projects (access:'owner') + received shared projects (access:'view_only')
  // Deduplicate received shares by history_id (keep the first/latest) so a project
  // shared multiple times never creates two rows with the same id.
  const uniqueShares = Array.from(
    new Map(receivedShares.map(s => [s.history_id, s])).values()
  );
  const ownProjectIds = new Set(
    Array.isArray(chatHistory) ? chatHistory.map(p => p.id) : []
  );
  const allProjects = [
    ...(Array.isArray(chatHistory) ? chatHistory.map(p => ({ ...p, access: 'owner' })) : []),
    ...uniqueShares
      .filter(s => !ownProjectIds.has(s.history_id))
      .map(s => ({
        id: s.history_id,
        history_name: s.project_name,
        task_name: s.task_name || '',
        task_id: s.task_id || null,
        timestamp: s.created_at,
        access: 'view_only',
        share_token: s.share_token,
        shared_by_email: s.shared_by_email,
      })),
  ];

  // Filter and sort projects based on search, URL agent filter, and modal filters
  const filteredProjects = allProjects
        .filter(item => {
          // URL agent filter
          if (agentFilter && item.task_id?.toString() !== agentFilter) return false;
          // Modal agent filter
          if (projFilterAgents.length > 0 && !projFilterAgents.includes(item.task_id?.toString())) return false;
          // Search
          const title = item.history_name || item.task_name || '';
          const agent = item.task_name || '';
          if (searchQuery && !title.toLowerCase().includes(searchQuery.toLowerCase()) && !agent.toLowerCase().includes(searchQuery.toLowerCase())) return false;
          // Date filter
          if (projFilterDate) {
            const ts = new Date(item.timestamp || 0);
            const now = new Date();
            if (projFilterDate === 'today') {
              if (ts.toDateString() !== now.toDateString()) return false;
            } else if (projFilterDate === '7days') {
              if (now - ts > 7 * 86400000) return false;
            } else if (projFilterDate === '30days') {
              if (now - ts > 30 * 86400000) return false;
            }
          }
          // Month filter
          if (projFilterMonth) {
            const ts = new Date(item.timestamp || 0);
            if (ts.getMonth() !== parseInt(projFilterMonth)) return false;
          }
          // Progress filter — bucket each project by its own agent's question
          // count, divided into thirds. Projects without questions are skipped.
          if (projFilterProgress) {
            const { completed, total } = getCompletedQuestions(item.id, item.task_id);
            if (total <= 0) return false;
            const lowMax = Math.floor(total / 3);
            const medMax = Math.floor((2 * total) / 3);
            if (projFilterProgress === 'low' && completed > lowMax) return false;
            if (projFilterProgress === 'medium' && (completed <= lowMax || completed > medMax)) return false;
            if (projFilterProgress === 'high' && completed <= medMax) return false;
          }
          return true;
        })
        .sort((a, b) => {
          // Most recent chat message first (falls back to timestamp —
          // creation time — for shared projects, which have no activity
          // timestamp of their own, and for rows predating this column).
          const da = new Date(a.last_activity_at || a.timestamp || 0).getTime();
          const db = new Date(b.last_activity_at || b.timestamp || 0).getTime();
          return projFilterSort === 'oldest' ? da - db : db - da;
        });

  // Split by tab
  const myProjects = filteredProjects.filter(p => p.access !== 'view_only');
  const sharedProjects = filteredProjects.filter(p => p.access === 'view_only');
  const activeProjects = projectsTab === 'my' ? myProjects : sharedProjects;

  // Pagination
  const totalPages = Math.ceil(activeProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProjects = activeProjects.slice(startIndex, startIndex + itemsPerPage);

  // Handle delete click
  const handleDeleteClick = (project) => {
    setDeleteModal({ isOpen: true, project });
    setOpenDropdown(null);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!deleteModal.project) return;
    try {
      setDeletingId(deleteModal.project.id);
      await deleteHistory(deleteModal.project.id);
      dispatch(projectDeleted(deleteModal.project.id));
      setDeleteModal({ isOpen: false, project: null });
    } catch (error) {
      console.error('Failed to delete project:', error);
    } finally {
      setDeletingId(null);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, project: null, isBulk: false });
  };

  // Handle edit start
  const handleEditStart = (project) => {
    setEditingProject(project.id);
    setEditTitle(project.history_name || project.task_name || '');
    setOpenDropdown(null);
  };

  // Handle edit save
  const handleEditSave = async (projectId) => {
    try {
      await updateHistoryName(projectId, editTitle);
      dispatch(projectRenamed({ id: projectId, history_name: editTitle }));
      setEditingProject(null);
      setEditTitle('');
    } catch (error) {
      console.error('Failed to update project name:', error);
    }
  };

  // Handle view detail (Continue to Analysis)
  const handleViewDetail = (projectId) => {
    if (userData?.user_role === 'No License') {
      navigate('/hilfe');
      return;
    }
    navigate(`/analysis/${projectId}`);
    setOpenDropdown(null);
  };

  // Mark share notification as read and update Nav badge
  const markShareNotifRead = (shareToken) => {
    const token = localStorage.getItem('token')
    if (!token || !shareToken) return
    fetch(`${BASE_SHARES_URL}/notifications/mark-by-link`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action_link: `/shared/${shareToken}` }),
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        if (data.unread_count !== undefined) {
          window.dispatchEvent(new CustomEvent('notifCountChange', { detail: { count: data.unread_count } }))
        }
      })
      .catch(() => {})
  }

  // Handle view project detail page — respects per-agent overviewEnabled toggle
  // (the dedicated "AI Request Overview" switch, not the unrelated Standard
  // Questions toggle - that one only hides the Questions tab/progress badges).
  const handleViewProjectDetail = (projectId, projectName, taskId = null) => {
    if (userData?.user_role === 'No License') {
      navigate('/hilfe');
      return;
    }
    const taskIdStr = taskId ? taskId.toString() : null;
    const showDetail = !overviewDisabled && (taskIdStr !== null
      ? (overviewEnabledByTask[taskIdStr] !== false)
      : true);
    if (showDetail) {
      navigate(`/project-detail/${projectId}`, { state: { projectName } });
    } else {
      navigate(`/analysis/${projectId}`);
    }
    setOpenDropdown(null);
  };

  // Toggle select all for current page
  const toggleSelectAll = () => {
    if (selectionModeActive) {
      // Disable selection mode and clear all selections
      setSelectionModeActive(false);
      setSelectedProjects(new Set());
    } else {
      // Enable selection mode and select all on current page
      setSelectionModeActive(true);
      const currentPageIds = paginatedProjects.map(p => p.id);
      setSelectedProjects(new Set(currentPageIds));
    }
    setShowHeaderDropdown(false);
  };

  // Toggle individual project selection
  const toggleProjectSelection = (projectId) => {
    const newSelected = new Set(selectedProjects);
    if (newSelected.has(projectId)) {
      newSelected.delete(projectId);
    } else {
      newSelected.add(projectId);
    }
    setSelectedProjects(newSelected);
  };

  // Check if all projects on current page are selected
  const allCurrentPageSelected = paginatedProjects.length > 0 &&
    paginatedProjects.every(p => selectedProjects.has(p.id));

  // Handle bulk delete click
  const handleBulkDeleteClick = () => {
    setDeleteModal({ isOpen: true, project: null, isBulk: true });
  };

  // Confirm bulk delete
  const confirmBulkDelete = async () => {
    try {
      setDeletingId('bulk');
      const idsToDelete = Array.from(selectedProjects);
      const deletePromises = idsToDelete.map(id => deleteHistory(id));
      await Promise.all(deletePromises);
      dispatch(projectDeleted(idsToDelete));
      setSelectedProjects(new Set());
      setDeleteModal({ isOpen: false, project: null, isBulk: false });
    } catch (error) {
      console.error('Failed to delete projects:', error);
    } finally {
      setDeletingId(null);
    }
  };

  // Watch for SharePoint sync results and capture imported files
  useEffect(() => {
    if (syncResults && syncResults.results) {
      const successfulFiles = syncResults.results
        .filter(r => r.status === 'synced')
        .map(r => ({
          id: r.file_id || Date.now() + Math.random(),
          name: r.file_name,
          pageCount: r.page_count || 0,
          status: 'synced'
        }));

      const failedFiles = syncResults.results
        .filter(r => r.status === 'error')
        .map(r => ({
          id: Date.now() + Math.random(),
          name: r.file_name,
          error: r.error,
          status: 'error'
        }));

      if (successfulFiles.length > 0 || failedFiles.length > 0) {
        setImportedSharePointFiles(prev => [...prev, ...successfulFiles, ...failedFiles]);
      }
    }
  }, [syncResults]);

  // Create Project Modal functions
  const openCreateProjectModal = () => {
    setCreateProjectModal(true);
    setImportedSharePointFiles([]);
    setNewProject({
      agent: agentFilter || '', // Pre-select agent if filtering by agent
      projectName: '',
      projectPicture: null,
      uploadMethod: '',
      selectedFolders: [],
      uploadedFiles: [],
      selectedSharePointFiles: [],
      sharePointConfigured: false
    });
  };

  const closeCreateProjectModal = () => {
    setCreateProjectModal(false);
  };

  // Direct Chat Mode (Project Setup toggle): skip the request-setup form
  // entirely — create a session with a placeholder name and jump straight
  // into chat. The session gets renamed automatically from the user's first
  // question (handled in ChatInput.jsx).
  const startDirectChat = async (agent) => {
    const organizationId = userData?.organization_id;
    if (!organizationId) {
      toast.error(t('homepage.noOrganization'));
      return;
    }
    if (isCreatingProject || isStartingDirectChat) return;
    try {
      setIsCreatingProject(true);
      setIsStartingDirectChat(true);
      const taskId = agent.id.toString();

      const historyId = uuidv4();
      const placeholderName = t('homepage.newChatPlaceholderName');
      await createChatHistory(user_id, taskId, historyId, agent.name || '', placeholderName, 'analyse');
      const nowIso = new Date().toISOString();
      dispatch(projectCreated({
        id: historyId, task_id: taskId, user_id, task_name: agent.name || '',
        history_name: placeholderName, timestamp: nowIso, project_image: null, last_activity_at: nowIso,
      }));
      navigate(`/analysis/${historyId}`);
    } catch (error) {
      console.error('Error starting direct chat:', error);
      if (error?.code === 'history_limit_reached') {
        toast.error(t('homepage.historyLimitReached', { limit: error.limit }));
      } else {
        toast.error(error?.message || t('homepage.startDirectChatError'));
      }
    } finally {
      setIsCreatingProject(false);
      setIsStartingDirectChat(false);
    }
  };

  const handleProjectPictureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewProject(prev => ({ ...prev, projectPicture: reader.result, projectPictureFile: file }));
      };
      reader.readAsDataURL(file);
    }
  };

  const deleteProjectPicture = () => {
    setNewProject(prev => ({ ...prev, projectPicture: null, projectPictureFile: null }));
  };

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    const processedFiles = [];
    // Counts across the WHOLE session's uploadedFiles so far, not just this
    // click — a user picking images across several separate clicks could
    // otherwise exceed the cap by adding a few at a time.
    let acceptedImageCount = newProject.uploadedFiles.filter(f => f.type?.startsWith('image/')).length;

    for (const file of files) {
      let pageCount = 0;

      // Count pages for PDF files
      if (file.type === 'application/pdf') {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          pageCount = pdf.numPages;
          await pdf.destroy();
        } catch (error) {
          console.error(`Error counting pages for ${file.name}:`, error);
          toast.error(`Fehler beim Verarbeiten von ${file.name}`);
          continue;
        }
      } else if (file.type?.startsWith('image/')) {
        // Document Intelligence analyzes an image directly — no pages to
        // split/count like a PDF, so this is always exactly 1 for OCR quota
        // purposes (matches ChatInput.jsx/Sidebar.jsx's identical handling).
        if (acceptedImageCount >= 3) {
          toast.error(t('chatActions.tooManyImages', { max: 3 }));
          continue;
        }
        pageCount = 1;
        acceptedImageCount += 1;
      }

      processedFiles.push({
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        file: file,
        pageCount: pageCount
      });
    }

    setNewProject(prev => ({
      ...prev,
      uploadedFiles: [...prev.uploadedFiles, ...processedFiles]
    }));
    // Reset input
    e.target.value = '';
  };

  const removeUploadedFile = (fileId) => {
    setNewProject(prev => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles.filter(f => f.id !== fileId)
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Handle SharePoint browser close
  const handleSharePointBrowserClose = () => {
    setShowSharePointBrowser(false);
    setNewProject(prev => ({ ...prev, uploadMethod: 'sharepoint', sharePointConfigured: true }));

    // Clear sync results from Redux
    dispatch(clearSyncResults());

    if (importedSharePointFiles.some(f => f.status === 'synced')) {
      toast.success('SharePoint-Dateien erfolgreich importiert!');
    } else if (importedSharePointFiles.some(f => f.status === 'error')) {
      toast.error('Einige Dateien konnten nicht importiert werden. Überprüfen Sie die Liste unten.');
    }
  };

  const handleAnalyze = async () => {
    if (!newProject.agent) {
      toast.error(t('toast.selectAgent'));
      return;
    }

    // When the agent has Project Setup custom fields, default Project Name input
    // is hidden — skip its validation. Otherwise it is still required.
    const useCustomMode = customFields.length > 0;
    if (!useCustomMode && !newProject.projectName) {
      toast.error(t('toast.enterProjectName'));
      return;
    }

    // Validate required custom fields.
    const errs = {};
    customFields.forEach((f) => {
      if (!f.required) return;
      const raw = customFieldValues[f.id];
      if (f.field_type === 'image' || f.field_type === 'file') {
        if (!Array.isArray(raw) || raw.length === 0) errs[f.id] = t('projectSetup.requiredError');
      } else {
        const v = (raw ?? '').toString().trim();
        if (!v) errs[f.id] = t('projectSetup.requiredError');
      }
    });
    if (Object.keys(errs).length > 0) {
      setCustomFieldErrors(errs);
      toast.error(t('projectSetup.requiredError'));
      return;
    }

    try {
      setIsCreatingProject(true);

      // Generate new history ID
      const historyId = uuidv4();

      // Get the selected agent/task details — use agentsForModal so admin can
      // find agents that have no department assigned (also visible in modal).
      const selectedAgent = agentsForModal.find(a => a.id.toString() === newProject.agent.toString());
      const taskName = selectedAgent?.name || '';

      // Resolve project name: prefer a custom text field whose label contains
      // "name" (case-insensitive). Otherwise use the default Project Name input,
      // or fall back to "agent name – timestamp".
      let resolvedProjectName = newProject.projectName;
      if (useCustomMode) {
        const nameField = customFields.find((f) =>
          (f.field_type === 'text' || f.field_type === 'textarea') &&
          /name/i.test(f.field_label || '')
        );
        const fromField = nameField ? (customFieldValues[nameField.id] || '').toString().trim() : '';
        resolvedProjectName = fromField || `${taskName || t('analysis.projectFallback')} – ${new Date().toLocaleString('de-DE')}`;
      }

      // Create chat history
      const chatHistoryResult = await createChatHistory(
        user_id,
        newProject.agent,
        historyId,
        taskName,
        resolvedProjectName,
        'analyse'
      );

      console.log('Chat history created:', chatHistoryResult);

      // Upload project image if selected (skipped in custom-fields mode).
      if (!useCustomMode && newProject.projectPicture && newProject.projectPictureFile) {
        try {
          const imgFormData = new FormData()
          imgFormData.append('file', newProject.projectPictureFile)
          imgFormData.append('history_id', historyId)
          await fetch(`${BASE_URL}/projects/upload-image`, { method: 'POST', headers: { ...authHeader() }, body: imgFormData })
        } catch (imgErr) {
          console.error('Project image upload failed:', imgErr)
        }
      }

      // Upload default files if any (skipped in custom-fields mode — those use
      // their own per-field upload endpoints).
      if (!useCustomMode) {
        const filesToUpload = newProject.uploadedFiles.map(f => f.file);
        if (filesToUpload.length > 0) {
          const pageCounts = newProject.uploadedFiles.map(f => f.pageCount || 0);
          const uploadResult = await uploadFilesWithHistory(
            filesToUpload,
            user_id,
            newProject.agent,
            historyId,
            pageCounts
          );
          console.log('Files uploaded:', uploadResult);
        }
      }

      // Save custom Project Setup field values (text + uploaded image/file refs).
      if (customFields.length > 0) {
        try {
          const valuesPayload = [];
          for (const f of customFields) {
            const raw = customFieldValues[f.id];
            if (f.field_type === 'image') {
              const fileList = Array.isArray(raw) ? raw : [];
              const blobPaths = [];
              for (const file of fileList) {
                const fd = new FormData();
                fd.append('file', file);
                try {
                  const res = await axios.post(
                    `${BASE_URL}/custom-field-images/${historyId}/${f.id}`,
                    fd,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                  );
                  if (res.data?.blob_path) blobPaths.push(res.data.blob_path);
                } catch (upErr) {
                  console.error('Custom-field image upload failed:', upErr);
                }
              }
              valuesPayload.push({ field_id: f.id, value: JSON.stringify(blobPaths) });
            } else if (f.field_type === 'file') {
              const fileList = Array.isArray(raw) ? raw : [];
              const refs = [];
              for (const file of fileList) {
                const fd = new FormData();
                fd.append('file', file);
                try {
                  const res = await axios.post(
                    `${BASE_URL}/custom-field-files/${historyId}/${f.id}`,
                    fd,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                  );
                  if (res.data?.blob_path) {
                    refs.push({
                      blob_path: res.data.blob_path,
                      filename: res.data.filename || file.name,
                    });
                  }
                } catch (upErr) {
                  console.error('Custom-field file upload failed:', upErr);
                }
              }
              valuesPayload.push({ field_id: f.id, value: JSON.stringify(refs) });
            } else {
              valuesPayload.push({ field_id: f.id, value: (raw ?? '').toString() });
            }
          }
          await axios.put(`${BASE_URL}/project-custom-field-values/${historyId}`, { values: valuesPayload }, authHeaders());
        } catch (cfErr) {
          console.error('Custom field values save failed:', cfErr);
        }
      }

      // Close modal and navigate to analysis page
      const nowIso = new Date().toISOString();
      dispatch(projectCreated({
        id: historyId, task_id: newProject.agent, user_id, task_name: taskName,
        history_name: resolvedProjectName, timestamp: nowIso, project_image: null, last_activity_at: nowIso,
      }));
      closeCreateProjectModal();
      toast.success(t('toast.projectCreated'));
      navigate(`/analysis/${historyId}`);

    } catch (error) {
      console.error('Error creating project:', error);
      let errorMessage = t('homepage.createProjectError');
      if (error?.code === 'history_limit_reached') {
        errorMessage = t('homepage.historyLimitReached', { limit: error.limit });
      } else if (error?.code === 'upload_page_limit_exceeded') {
        errorMessage = t('chatActions.uploadPageLimitExceeded', { limit: error.limit, attempted: error.attempted });
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setIsCreatingProject(false);
    }
  };

  return (
    <div className="h-screen bg-white overflow-hidden flex flex-col">
      <Toaster position="top-right" />
      {isStartingDirectChat && (
        <div className="fixed inset-0 bg-white/70 backdrop-blur-[1px] z-[400] flex flex-col items-center justify-center gap-3">
          <div
            className="w-10 h-10 border-[3px] border-t-transparent rounded-full animate-spin"
            style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}
          />
          <p className="text-[14px] font-medium text-[#374151]">{t('homepage.startingDirectChat')}</p>
        </div>
      )}
      {/* Page-level title — same top-left placement as other sidebar pages.
          Replaced by a deeper breadcrumb only when filtering by a specific agent. */}
      {agentFilter ? (
        <div className="px-10 py-5 flex items-center gap-2 text-[14px] flex-shrink-0">
          <button onClick={() => navigate('/')} className="flex items-center gap-1 text-[#6B7280] hover:text-[#374151]">
            <Home size={16} />
            <span>{t('nav.homepage')}</span>
          </button>
          <span className="text-[#9CA3AF]">/</span>
          <button onClick={() => navigate('/history')} className="text-[#6B7280] hover:text-[#374151]">
            {t('history.pageTitle')}
          </button>
          <span className="text-[#9CA3AF]">/</span>
          <span className="text-[#111827] font-medium">{agentNameFromUrl || 'Agent'}</span>
        </div>
      ) : (
        <header className="px-10 h-[80px] flex items-center justify-between flex-shrink-0">
          <h1 className="text-[22px] font-bold text-[#111827]">{t('history.pageTitle')}</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={openCreateProjectModal}
              disabled={!hasUsableAgents}
              title={!hasUsableAgents ? t('homepage.createAgentFirst') : undefined}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-[14px] font-medium transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
              style={{ backgroundColor: aispaceColor }}
            >
              <Plus size={18} strokeWidth={2.5} />
              {t('history.createProject')}
            </button>
            {/* Delete All Button - shown when items are selected */}
            {selectedProjects.size > 0 && (
              <button
                onClick={handleBulkDeleteClick}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-[14px] font-medium bg-[#D32F2F] hover:bg-[#DC2626] transition-colors"
              >
                {t('history.deleteAll')}
              </button>
            )}
            {/* Header Dropdown */}
            <div className="relative" ref={headerDropdownRef}>
              <button
                onClick={() => setShowHeaderDropdown(!showHeaderDropdown)}
                className="p-2 text-[#6B7280] hover:text-[#374151] border border-[#EEEEEE] rounded-lg hover:bg-[#F9FAFB] transition-colors"
              >
                <MoreHorizontal size={20} />
              </button>
              {/* Dropdown Menu */}
              {showHeaderDropdown && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-[#EEEEEE] rounded-lg shadow-lg z-50 overflow-hidden">
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center gap-3 px-4 py-3 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                  >
                    {/* Checkbox icon */}
                    <div
                      className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                      style={selectionModeActive ? { backgroundColor: aispaceColor, borderColor: aispaceColor } : { borderColor: '#D1D5DB' }}
                    >
                      {selectionModeActive && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                          <path d="M20 6L9 17l-5-5"/>
                        </svg>
                      )}
                    </div>
                    <span>{t('agents.selectAll')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      <main className="px-10 pb-10 flex-1 overflow-hidden flex flex-col">
        {/* Clear filter pill — only visible when filtering by an agent */}
        {agentFilter && (
          <div className="flex items-center mb-6 flex-shrink-0">
            <button
              onClick={() => navigate('/history')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-[#6B7280] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-full transition-colors"
            >
              <X size={14} />
              {t('history.clearFilter')}
            </button>
          </div>
        )}

        {/* Card Container with border */}
        <div className="bg-white border border-[#EEEEEE] rounded-xl flex-1 overflow-hidden flex flex-col">

        {/* Tab bar: My Projects / Shared Projects */}
        <div className="flex items-center gap-6 px-6 border-b border-[#EEEEEE] flex-shrink-0">
          <button
            onClick={() => { setProjectsTab('my'); setCurrentPage(1); }}
            className="py-3.5 text-[14px] font-medium relative transition-colors"
            style={{ color: projectsTab === 'my' ? aispaceColor : '#9E9E9E' }}
          >
            {t('history.tabMyProjects')}
            {projectsTab === 'my' && (
              <div className="absolute -bottom-px left-0 right-0 h-[2px] z-10" style={{ backgroundColor: aispaceColor }} />
            )}
          </button>
          <button
            onClick={() => { setProjectsTab('shared'); setCurrentPage(1); }}
            className="py-3.5 text-[14px] font-medium relative transition-colors flex items-center gap-2"
            style={{ color: projectsTab === 'shared' ? aispaceColor : '#9E9E9E' }}
          >
            {t('history.tabInvitedProjects')}
            {projectsTab === 'shared' && (
              <div className="absolute -bottom-px left-0 right-0 h-[2px] z-10" style={{ backgroundColor: aispaceColor }} />
            )}
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Search Input */}
            <div className="relative w-[400px]">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2.5 text-[14px] border border-[#EEEEEE] rounded-lg focus:outline-none focus:ring-0 transition-colors"
                onFocus={(e) => e.target.style.borderColor = aispaceColor}
                onBlur={(e) => e.target.style.borderColor = '#EEEEEE'}
              />
            </div>

            {/* Filters Button */}
            <button
              onClick={() => {
                setDraftAgents([...projFilterAgents])
                setDraftDate(projFilterDate)
                setDraftMonth(projFilterMonth)
                setDraftProgress(projFilterProgress)
                setDraftSort(projFilterSort)
                setProjFilterOpen(true)
              }}
              className={`flex items-center gap-2 px-4 py-2.5 text-[14px] font-medium rounded-lg transition-colors ${
                hasActiveFilter ? 'text-white' : 'text-[#374151] border border-[#EEEEEE] hover:bg-[#F9FAFB]'
              }`}
              style={hasActiveFilter ? { backgroundColor: aispaceColor } : {}}
            >
              <Plus size={16} />
              {t('history.filter')}{hasActiveFilter ? ` (${[projFilterAgents.length > 0, projFilterDate, projFilterMonth, projFilterProgress].filter(Boolean).length})` : ''}
            </button>
          </div>

          {/* View Toggle */}
          <div className="flex items-center border border-[#EEEEEE] rounded-lg overflow-hidden">
            <button
              onClick={() => {
                setViewMode('grid');
                setCurrentPage(1); // Reset to first page when switching views
              }}
              className={`p-2.5 transition-colors ${viewMode === 'grid' ? 'bg-[#F3F4F6] text-[#111827]' : 'text-[#6B7280] hover:bg-[#F9FAFB]'}`}
            >
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => {
                setViewMode('list');
                setCurrentPage(1); // Reset to first page when switching views
              }}
              className={`p-2.5 transition-colors ${viewMode === 'list' ? 'bg-[#F3F4F6] text-[#111827]' : 'text-[#6B7280] hover:bg-[#F9FAFB]'}`}
            >
              <List size={18} />
            </button>
          </div>
        </div>

        {/* Projects Content */}
        {status === 'loading' ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}></div>
          </div>
        ) : paginatedProjects.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-16">
            {/* Decorative circle with + icon */}
            <div className="relative mb-6">
              {/* Outer decorative ring with dots */}
              <div className="w-[120px] h-[120px] rounded-full border-2 border-dashed border-[#EEEEEE] flex items-center justify-center relative">
                {/* Decorative dots */}
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#EEEEEE]"></div>
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#EEEEEE]"></div>
                <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 rounded-full bg-[#EEEEEE]"></div>
                <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-2 h-2 rounded-full bg-[#EEEEEE]"></div>
                {/* Inner circle with + icon */}
                <div className="w-16 h-16 rounded-full bg-[#F9FAFB] border border-[#EEEEEE] flex items-center justify-center">
                  <Plus size={28} className="text-[#9CA3AF]" />
                </div>
              </div>
            </div>
            {/* Text */}
            <h3 className="text-[16px] font-semibold text-[#111827] mb-2">
              {searchQuery
                ? t('history.noProjectsFound')
                : agentFilter
                  ? t('history.noProjectsCreated')
                  : t('history.noProjectsCreated')}
            </h3>
            <p className="text-[14px] text-[#6B7280]">
              {searchQuery
                ? t('history.noProjectsSearchHint')
                : agentFilter
                  ? (agentNameFromUrl ? t('history.createProjectForAgent', { agent: agentNameFromUrl }) : t('history.createProjectForThisAgent'))
                  : t('history.createHint')}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View */
          <div className="flex-1 overflow-y-auto thin-scrollbar pr-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pb-4 px-6">
            {paginatedProjects.map((project, index) => {
              const { completed, total } = getCompletedQuestions(project.id, project.task_id);
              // Determine if dropdown should open upward
              // Show dropdown above for: last row items OR when there's only 1 row (3 or fewer items)
              const itemsPerRow = 3;
              const totalItems = paginatedProjects.length;
              const lastRowStartIndex = Math.floor((totalItems - 1) / itemsPerRow) * itemsPerRow;
              const isInLastRow = index >= lastRowStartIndex;
              const shouldDropdownOpenUp = totalItems <= itemsPerRow || isInLastRow;
              const isDropdownOpen = openDropdown === project.id;
              const isEditing = editingProject === project.id;
              const title = project.history_name || project.task_name || t('history.untitledProject');
              const agent = project.task_name || t('analysis.agentFallback');

              return (
                <div
                  key={project.id}
                  className="bg-white rounded-xl border border-[#EEEEEE] hover:shadow-md transition-shadow relative cursor-pointer"
                  onClick={() => {
                    if (selectionModeActive) {
                      toggleProjectSelection(project.id);
                      return;
                    }
                    if (isEditing) return;
                    if (project.access === 'view_only') {
                      markShareNotifRead(project.share_token)
                      navigate(`/shared/${project.share_token}`, { state: { mode: 'full' } });
                      return;
                    }
                    handleViewProjectDetail(project.id, project.history_name, project.task_id);
                  }}
                >
                  {/* Checkbox on card - only show when selection mode is active */}
                  {selectionModeActive && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleProjectSelection(project.id);
                      }}
                      className="absolute top-3 left-3 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors z-10"
                      style={selectedProjects.has(project.id) ? { backgroundColor: aispaceColor, borderColor: aispaceColor } : { backgroundColor: 'white', borderColor: '#D1D5DB' }}
                    >
                      {selectedProjects.has(project.id) && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                          <path d="M20 6L9 17l-5-5"/>
                        </svg>
                      )}
                    </button>
                  )}
                  {/* Card Banner - overflow-hidden here to clip rounded corners */}
                  <div className="overflow-hidden rounded-t-xl">
                    <ProjectImage projectId={project.id} imagePath={project.project_image} type="banner" />
                  </div>

                  {/* Card Content */}
                  <div className="p-4">
                    {/* Title */}
                    <div className="mb-3">
                      <p className="text-[12px] font-medium text-[#6B7280] mb-1">{t('history.titleLabel')}</p>
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="flex-1 px-2 py-1 text-[14px] border border-gray-300 rounded focus:outline-none focus:ring-0"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleEditSave(project.id);
                              if (e.key === 'Escape') {
                                setEditingProject(null);
                                setEditTitle('');
                              }
                            }}
                          />
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditSave(project.id); }}
                            className="p-1 text-green-600 hover:text-green-700"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 6L9 17l-5-5"/>
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingProject(null);
                              setEditTitle('');
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-[14px] text-[#111827] font-medium truncate">{title}</p>
                          {project.access === 'view_only' && (
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md flex-shrink-0" style={{ backgroundColor: `${aispaceColor}18`, color: aispaceColor }}>
                              {t('history.accessInvited')}
                            </span>
                          )}
                          {project.access !== 'view_only' && sharedHistoryIds.has(String(project.id)) && (
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md flex-shrink-0" style={{ backgroundColor: `${aispaceColor}18`, color: aispaceColor }}>
                              {t('history.accessViewOnly')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Agent */}
                    <div className="mb-3">
                      <p className="text-[12px] font-medium text-[#6B7280] mb-1">{t('history.agentLabel')}</p>
                      <p className="text-[14px] text-[#374151]">{agent}</p>
                    </div>

                    {/* Creation Date */}
                    <div className="mb-3">
                      <p className="text-[12px] font-medium text-[#6B7280] mb-1">{t('history.creationDateLabel')}</p>
                      <p className="text-[14px] text-[#374151]">{formatTimestamp(project.timestamp)}</p>
                    </div>

                    {/* Progress — hidden when standard questions disabled */}
                    {showProjectDetailByTask[project.task_id?.toString()] !== false && (
                      <div className="mb-4">
                        <p className="text-[12px] font-medium text-[#6B7280] mb-1">{t('history.progressLabel')}</p>
                        <p className="text-[14px] text-[#374151]">{t('history.answeredOf', { done: completed, total })}</p>
                      </div>
                    )}

                    {/* Antwort-Status — hidden when standard questions disabled */}
                    {showProjectDetailByTask[project.task_id?.toString()] !== false && (
                      <div className="mb-4">
                        <p className="text-[12px] font-medium text-[#6B7280] mb-1">{t('history.tableAnswerStatus')}</p>
                        <AnswerStatusBadges statuses={getProjectStatuses(project.id, project.task_id)} />
                      </div>
                    )}

                    {/* Actions */}
                    <div className="-mx-4 px-4 pt-4 border-t border-[#EEEEEE] flex items-center justify-between">
                      {project.access === 'view_only' ? (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); markShareNotifRead(project.share_token); navigate(`/shared/${project.share_token}`, { state: { mode: 'chat' } }); }}
                            className="px-4 py-2 text-[13px] font-medium text-white rounded-lg transition-colors hover:opacity-90"
                            style={{ backgroundColor: aispaceColor }}
                          >
                            {t('share.viewButton')}
                          </button>
                          <div className="relative" ref={isDropdownOpen ? dropdownRef : null}>
                            <button
                              onClick={(e) => { e.stopPropagation(); setOpenDropdown(isDropdownOpen ? null : project.id); }}
                              className="p-2 text-[#6B7280] hover:text-[#374151] border border-[#EEEEEE] rounded-lg transition-colors hover:bg-[#F9FAFB]"
                            >
                              <MoreHorizontal size={18} />
                            </button>
                            {isDropdownOpen && (
                              <div className={`absolute right-0 w-60 bg-white border border-[#EEEEEE] rounded-lg shadow-lg z-[999] py-1 ${shouldDropdownOpenUp ? 'bottom-full mb-2' : 'top-full mt-2'}`}>
                                {!overviewDisabled && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setOpenDropdown(null); markShareNotifRead(project.share_token); navigate(`/shared/${project.share_token}`, { state: { mode: 'full' } }); }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                                  >
                                    <Eye size={16} className="text-[#6B7280]" />
                                    <span>{t('history.projectDetails')}</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleViewDetail(project.id); }}
                            className="px-4 py-2 text-[13px] font-medium text-white rounded-lg transition-colors hover:opacity-90"
                            style={{ backgroundColor: aispaceColor }}
                          >
                            {t('history.continue')}
                          </button>
                          <div className="relative" ref={isDropdownOpen ? dropdownRef : null}>
                            <button
                              onClick={(e) => { e.stopPropagation(); setOpenDropdown(isDropdownOpen ? null : project.id); }}
                              className="p-2 text-[#6B7280] hover:text-[#374151] border border-[#EEEEEE] rounded-lg transition-colors hover:bg-[#F9FAFB]"
                            >
                              <MoreHorizontal size={18} />
                            </button>

                            {/* Dropdown Menu */}
                            {isDropdownOpen && (
                              <div className={`absolute right-0 w-60 bg-white border border-[#EEEEEE] rounded-lg shadow-lg z-[999] py-1 ${shouldDropdownOpenUp ? 'bottom-full mb-2' : 'top-full mt-2'}`}>
                                {!overviewDisabled && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleViewProjectDetail(project.id, project.history_name, project.task_id); }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                                  >
                                    <Eye size={16} className="text-[#6B7280]" />
                                    <span>{t('history.projectDetails')}</span>
                                  </button>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); setOpenDropdown(null); setShareTarget({ id: project.id, name: title }); }}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                                >
                                  <Share2 size={16} className="text-[#6B7280]" />
                                  <span>{t('share.shareProject')}</span>
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleEditStart(project); }}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                                >
                                  <Pencil size={16} className="text-[#6B7280]" />
                                  <span>{t('common.edit')}</span>
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDeleteClick({ id: project.id, title }); }}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
                                >
                                  <Trash2 size={16} />
                                  <span>{t('common.delete')}</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          </div>
        ) : (
          /* List View */
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Table Header — same grid template as the body rows below so
                every text column has an identical x-origin and width. The
                leading "auto" column holds the optional select-all checkbox.
                The 5 data columns are equal `1fr` shares; status/actions
                stay fixed-width because they hold pill badges and buttons. */}
            <div className="grid grid-cols-[minmax(200px,1fr)_80px_120px_110px_135px_150px_160px] items-center gap-4 px-6 py-3 border-b border-[#EEEEEE] bg-[#F5F5F5] min-w-[1000px] flex-shrink-0">
              <div className="flex items-center gap-3">
                {selectionModeActive && (
                  <button
                    onClick={toggleSelectAll}
                    className="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0"
                    style={allCurrentPageSelected ? { backgroundColor: aispaceColor, borderColor: aispaceColor } : { borderColor: '#D1D5DB' }}
                  >
                    {allCurrentPageSelected && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    )}
                  </button>
                )}
                <span className="text-[13px] font-medium text-[#6B7280]">{t('history.tableTitle')}</span>
              </div>
              <div></div>
              <div className="text-left text-[13px] font-medium text-[#6B7280]">{t('history.tableAgents')}</div>
              <div className="text-left text-[13px] font-medium text-[#6B7280]">{t('history.tableCreatedAt')}</div>
              <div className="text-left text-[13px] font-medium text-[#6B7280]">{t('history.tableProgress')}</div>
              <div className="text-left text-[13px] font-medium text-[#6B7280]">{t('history.tableAnswerStatus')}</div>
              <div></div>
            </div>

            {/* Table Body */}
            <div className="flex-1 overflow-y-auto overflow-x-auto thin-scrollbar">
              {paginatedProjects.map((project, index) => {
                const { completed, total } = getCompletedQuestions(project.id, project.task_id);
                // Only open dropdown upward for the last item when there are more than 3 items
                const isLastItem = paginatedProjects.length > 3 && index === paginatedProjects.length - 1;
                const isDropdownOpen = openDropdown === project.id;
                const isEditing = editingProject === project.id;
                const title = project.history_name || project.task_name || t('history.untitledProject');
                const agent = project.task_name || t('analysis.agentFallback');

                return (
                  <div
                    key={project.id}
                    className={`grid grid-cols-[minmax(200px,1fr)_80px_120px_110px_135px_150px_160px] items-center gap-4 px-6 py-4 hover:bg-[#FAFAFA] transition-colors min-w-[1000px] relative cursor-pointer ${
                      index !== paginatedProjects.length - 1 ? 'border-b border-[#EEEEEE]' : ''
                    }`}
                    onClick={() => {
                      if (selectionModeActive) {
                        toggleProjectSelection(project.id);
                        return;
                      }
                      if (isEditing) return;
                      if (project.access === 'view_only') {
                        markShareNotifRead(project.share_token)
                        navigate(`/shared/${project.share_token}`, { state: { mode: 'full' } });
                        return;
                      }
                      handleViewProjectDetail(project.id, project.history_name, project.task_id);
                    }}
                  >
                    {/* Title + icon column (merged, same as HomeScreen) */}
                    <div className="flex-1 min-w-[180px] flex items-center gap-3">
                      {selectionModeActive && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleProjectSelection(project.id);
                          }}
                          className="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0"
                          style={selectedProjects.has(project.id) ? { backgroundColor: aispaceColor, borderColor: aispaceColor } : { borderColor: '#D1D5DB' }}
                        >
                          {selectedProjects.has(project.id) && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                              <path d="M20 6L9 17l-5-5"/>
                            </svg>
                          )}
                        </button>
                      )}
                      <ProjectImage projectId={project.id} imagePath={project.project_image} type="icon" />
                      {isEditing ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="flex-1 px-2 py-1 text-[14px] border border-gray-300 rounded focus:outline-none focus:border-[#8B5CF6]"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleEditSave(project.id);
                              if (e.key === 'Escape') {
                                setEditingProject(null);
                                setEditTitle('');
                              }
                            }}
                          />
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditSave(project.id); }}
                            className="p-1 flex-shrink-0"
                            style={{ color: aispaceColor }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 6L9 17l-5-5"/>
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingProject(null);
                              setEditTitle('');
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <span className="text-[14px] font-medium text-[#111827] truncate">{title}</span>
                      )}
                    </div>

                    {/* Shared badge column */}
                    <div className="min-w-0">
                      {project.access === 'view_only' && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md" style={{ backgroundColor: `${aispaceColor}18`, color: aispaceColor }}>
                          {t('history.accessInvited')}
                        </span>
                      )}
                      {project.access !== 'view_only' && sharedHistoryIds.has(String(project.id)) && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md" style={{ backgroundColor: `${aispaceColor}18`, color: aispaceColor }}>
                          {t('history.accessViewOnly')}
                        </span>
                      )}
                    </div>

                    {/* Agent */}
                    <div className="text-left text-[14px] text-[#374151] truncate min-w-0">
                      {agent}
                    </div>

                    {/* Date */}
                    <div className="text-left text-[14px] text-[#6B7280] min-w-0 truncate">
                      {formatTimestamp(project.timestamp)}
                    </div>

                    {/* Progress */}
                    <div className="text-left text-[14px] text-[#6B7280] min-w-0 truncate">
                      {showProjectDetailByTask[project.task_id?.toString()] !== false
                        ? t('history.answeredOf', { done: completed, total })
                        : <span className="block text-center">—</span>}
                    </div>

                    {/* Antwort-Status */}
                    <div className="min-w-0">
                      {showProjectDetailByTask[project.task_id?.toString()] !== false
                        ? <AnswerStatusBadges statuses={getProjectStatuses(project.id, project.task_id)} size="xs" />
                        : <span className="block text-center text-[14px] text-[#6B7280]">—</span>}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-2 flex-shrink-0">
                      {project.access === 'view_only' ? (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); markShareNotifRead(project.share_token); navigate(`/shared/${project.share_token}`, { state: { mode: 'chat' } }); }}
                            className="px-4 py-1.5 text-[13px] font-medium text-white rounded-md transition-colors hover:opacity-90"
                            style={{ backgroundColor: aispaceColor }}
                          >
                            {t('share.viewButton')}
                          </button>
                          <div className="relative" ref={isDropdownOpen ? dropdownRef : null}>
                            <button
                              onClick={(e) => { e.stopPropagation(); setOpenDropdown(isDropdownOpen ? null : project.id); }}
                              className="p-2 text-[#9CA3AF] hover:text-[#6B7280] border border-[#EEEEEE] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                            >
                              <MoreVertical size={18} />
                            </button>
                            {isDropdownOpen && (
                              <div className={`absolute right-0 ${isLastItem ? 'bottom-full mb-2' : 'top-full mt-2'} w-60 bg-white border border-[#EEEEEE] rounded-lg shadow-xl z-[999] py-1`}>
                                {!overviewDisabled && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setOpenDropdown(null); markShareNotifRead(project.share_token); navigate(`/shared/${project.share_token}`, { state: { mode: 'full' } }); }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                                  >
                                    <Eye size={16} className="text-[#6B7280]" />
                                    <span>{t('history.projectDetails')}</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleViewDetail(project.id); }}
                            className="px-4 py-1.5 text-[13px] font-medium text-white rounded-md transition-colors hover:opacity-90"
                            style={{ backgroundColor: aispaceColor }}
                          >
                            {t('history.continue')}
                          </button>
                          <div className="relative" ref={isDropdownOpen ? dropdownRef : null}>
                            <button
                              onClick={(e) => { e.stopPropagation(); setOpenDropdown(isDropdownOpen ? null : project.id); }}
                              className="p-2 text-[#9CA3AF] hover:text-[#6B7280] border border-[#EEEEEE] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                            >
                              <MoreVertical size={18} />
                            </button>

                            {/* Dropdown Menu */}
                            {isDropdownOpen && (
                              <div
                                className={`absolute right-0 ${isLastItem ? 'bottom-full mb-2' : 'top-full mt-2'} w-60 bg-white border border-[#EEEEEE] rounded-lg shadow-xl z-[999] py-1`}
                              >
                                {!overviewDisabled && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleViewProjectDetail(project.id, project.history_name, project.task_id); }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                                  >
                                    <Eye size={16} className="text-[#6B7280]" />
                                    <span>{t('history.projectDetails')}</span>
                                  </button>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); setOpenDropdown(null); setShareTarget({ id: project.id, name: title }); }}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                                >
                                  <Share2 size={16} className="text-[#6B7280]" />
                                  <span>{t('share.shareProject')}</span>
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleEditStart(project); }}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                                >
                                  <Pencil size={16} className="text-[#6B7280]" />
                                  <span>{t('common.edit')}</span>
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDeleteClick({ id: project.id, title }); }}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#EF4444] hover:bg-[#FEF2F2] transition-colors whitespace-nowrap"
                                >
                                  <Trash2 size={16} />
                                  <span>{t('common.delete')}</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pagination */}
        {filteredProjects.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-[#EEEEEE] flex-shrink-0">
            <span className="text-[14px] text-[#6B7280]">
              {t('pagination.pageOf', { current: currentPage, total: totalPages || 1 })}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-[14px] font-medium text-[#374151] border border-[#EEEEEE] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.back')}
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-4 py-2 text-[14px] font-medium text-[#374151] border border-[#EEEEEE] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={cancelDelete}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-xl w-[400px] p-6 mx-4">
            {/* Warning Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-[#FEE2E2] flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#EF4444" strokeWidth="2"/>
                  <path d="M12 8V12" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="16" r="1" fill="#EF4444"/>
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-[18px] font-semibold text-[#111827] text-center mb-2">
              {deleteModal.isBulk
                ? t('deleteModal.bulkTitle', { count: selectedProjects.size })
                : t('deleteModal.singleTitle')}
            </h3>

            {/* Description */}
            <p className="text-[14px] text-[#6B7280] text-center mb-6">
              {deleteModal.isBulk
                ? t('deleteModal.bulkDescription', { count: selectedProjects.size })
                : t('deleteModal.singleDescription')}
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 px-4 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={deleteModal.isBulk ? confirmBulkDelete : confirmDelete}
                disabled={deletingId}
                className="flex-1 px-4 py-2.5 text-[14px] font-medium text-white bg-[#EF4444] rounded-lg hover:bg-[#DC2626] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deletingId ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('deleteModal.deleting')}
                  </>
                ) : (
                  deleteModal.isBulk ? t('history.deleteAll') : t('common.delete')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      {createProjectModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={isCreatingProject ? undefined : closeCreateProjectModal}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-xl w-[520px] max-h-[90vh] mx-4 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB]">
              <h2 className="text-[20px] font-semibold text-[#111827]">{t('createProjectModal.title')}</h2>
              <button
                onClick={closeCreateProjectModal}
                disabled={isCreatingProject}
                className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors disabled:opacity-50"
              >
                <X size={20} className="text-[#6B7280]" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* Agent Dropdown - custom */}
              <div>
                <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('createProjectModal.agentLabel')}</label>
                <div className="relative" ref={histAgentDropdownRef}>
                  {(() => {
                    const selectedAgent = agentsForModal.find(a => a.id.toString() === newProject.agent)
                    const label = selectedAgent ? selectedAgent.name : t('createProjectModal.agentPlaceholder')
                    return (
                      <>
                        <button
                          type="button"
                          ref={histAgentTriggerRef}
                          onClick={() => {
                            if (!histAgentDropdownOpen && histAgentTriggerRef.current) {
                              const r = histAgentTriggerRef.current.getBoundingClientRect();
                              setHistAgentMenuPos({ top: r.bottom + 4, left: r.left, width: r.width });
                            }
                            setHistAgentDropdownOpen(v => !v);
                          }}
                          className="w-full flex items-center justify-between px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg bg-white hover:bg-[#F9FAFB] focus:outline-none transition-colors cursor-pointer"
                          style={histAgentDropdownOpen ? { borderColor: aispaceColor } : {}}
                        >
                          <span className={selectedAgent ? 'text-[#111827]' : 'text-[#9CA3AF]'}>{label}</span>
                          <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${histAgentDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {histAgentDropdownOpen && (
                          <div
                            className="bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-[300] max-h-[260px] overflow-y-auto py-1"
                            style={{ position: 'fixed', top: histAgentMenuPos?.top, left: histAgentMenuPos?.left, width: histAgentMenuPos?.width }}
                          >
                            {agentsForModal.length === 0 ? (
                              <div className="px-4 py-3 text-[13px] text-[#9CA3AF]">{t('createProjectModal.noAgentsAvailable')}</div>
                            ) : (
                              agentsForModal.map(agent => {
                                const isSelected = agent.id.toString() === newProject.agent
                                return (
                                  <button
                                    key={agent.id}
                                    type="button"
                                    onClick={() => {
                                      setHistAgentDropdownOpen(false)
                                      if (agent.direct_chat_mode) {
                                        closeCreateProjectModal()
                                        startDirectChat(agent)
                                        return
                                      }
                                      setNewProject(prev => ({ ...prev, agent: agent.id.toString() }))
                                    }}
                                    className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors ${isSelected ? 'bg-[#F3F4F6]' : ''}`}
                                  >
                                    <span className="truncate">{agent.name}</span>
                                    {isSelected && <Check size={16} style={{ color: aispaceColor }} />}
                                  </button>
                                )
                              })
                            )}
                          </div>
                        )}
                      </>
                    )
                  })()}
                </div>
              </div>

              {/* All other fields are revealed only after an agent is chosen. */}
              {newProject.agent && (
              <>
              {/* While the agent's custom-field schema is loading, show a
                  spinner instead of momentarily flashing the default fields. */}
              {customFieldsLoading && (
                <div className="flex items-center justify-center py-10">
                  <div
                    className="w-7 h-7 border-2 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}
                  />
                </div>
              )}
              {/* Default fields (Project Picture + Name) — only shown once
                  loading is done AND the agent has no custom fields. */}
              {!customFieldsLoading && customFields.length === 0 && (
              <>
              {/* Project Picture */}
              <div>
                <label className="block text-[14px] font-semibold text-[#111827] mb-3">{t('createProjectModal.projectImageLabel')}</label>
                <div className="flex items-center gap-4">
                  {/* Preview */}
                  <div className="w-[72px] h-[72px] rounded-full bg-gradient-to-br from-[#E0F2FE] via-[#DBEAFE] to-[#EDE9FE] flex items-center justify-center overflow-hidden">
                    {newProject.projectPicture ? (
                      <img src={newProject.projectPicture} alt="Project" className="w-full h-full object-cover" />
                    ) : (
                      <svg className="w-10 h-10 opacity-80" viewBox="0 0 40 40" fill="none">
                        <path d="M20 10L10 18L20 26L30 18L20 10Z" fill="url(#previewGrad1)" />
                        <path d="M10 24L20 32L30 24" stroke="url(#previewGrad2)" strokeWidth="2" strokeLinecap="round"/>
                        <defs>
                          <linearGradient id="previewGrad1" x1="10" y1="10" x2="30" y2="26" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#38BDF8"/><stop offset="1" stopColor="#818CF8"/>
                          </linearGradient>
                          <linearGradient id="previewGrad2" x1="10" y1="24" x2="30" y2="32" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#60A5FA"/><stop offset="1" stopColor="#A78BFA"/>
                          </linearGradient>
                        </defs>
                      </svg>
                    )}
                  </div>

                  {/* Upload Button */}
                  <input
                    type="file"
                    ref={projectPictureRef}
                    onChange={handleProjectPictureUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    onClick={() => projectPictureRef.current?.click()}
                    className="px-5 py-2.5 text-[14px] font-medium text-white rounded-lg transition-colors hover:opacity-90"
                    style={{ backgroundColor: aispaceColor }}
                  >
                    {newProject.projectPicture ? t('createProjectModal.uploadNewImage') : t('createProjectModal.uploadImage')}
                  </button>

                  {newProject.projectPicture ? (
                    <button
                      onClick={deleteProjectPicture}
                      className="px-4 py-2.5 text-[14px] font-medium text-[#EF4444] hover:text-[#DC2626] transition-colors"
                    >
                      {t('common.delete')}
                    </button>
                  ) : (
                    <span className="text-[14px] text-[#9CA3AF]">{t('createProjectModal.noFileSelected')}</span>
                  )}
                </div>
              </div>

              {/* Project Name */}
              <div>
                <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('createProjectModal.projectNameLabel')}</label>
                <input
                  type="text"
                  value={newProject.projectName}
                  onChange={(e) => setNewProject(prev => ({ ...prev, projectName: e.target.value }))}
                  placeholder={t('createProjectModal.projectNamePlaceholder')}
                  className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none"
                  onFocus={(e) => e.target.style.borderColor = aispaceColor}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              </>
              )}

              {/* Custom Project Setup fields (per agent) */}
              {customFields.map((field) => {
                const error = customFieldErrors[field.id];
                if (field.field_type === 'image') {
                  const fileList = Array.isArray(customFieldValues[field.id]) ? customFieldValues[field.id] : [];
                  return (
                    <CustomImageField
                      key={field.id}
                      field={field}
                      value={fileList}
                      onChange={(files) => {
                        setCustomFieldValues((prev) => ({ ...prev, [field.id]: files }));
                        if (error) setCustomFieldErrors((prev) => ({ ...prev, [field.id]: null }));
                      }}
                      error={error}
                      aispaceColor={aispaceColor}
                    />
                  );
                }
                if (field.field_type === 'file') {
                  const fileList = Array.isArray(customFieldValues[field.id]) ? customFieldValues[field.id] : [];
                  return (
                    <CustomFileField
                      key={field.id}
                      field={field}
                      value={fileList}
                      onChange={(files) => {
                        setCustomFieldValues((prev) => ({ ...prev, [field.id]: files }));
                        if (error) setCustomFieldErrors((prev) => ({ ...prev, [field.id]: null }));
                      }}
                      error={error}
                      aispaceColor={aispaceColor}
                      onSharePoint={() => {
                        if (!newProject.agent) {
                          toast.error(t('toast.selectAgentFirst'));
                          return;
                        }
                        setShowSharePointBrowser(true);
                      }}
                    />
                  );
                }
                const value = customFieldValues[field.id] ?? '';
                const set = (v) => {
                  setCustomFieldValues((prev) => ({ ...prev, [field.id]: v }));
                  if (error) setCustomFieldErrors((prev) => ({ ...prev, [field.id]: null }));
                };
                const baseInputCls = `w-full px-4 py-3 text-[14px] border rounded-lg focus:outline-none ${error ? 'border-[#EF4444]' : 'border-[#E5E7EB]'}`;
                let opts = [];
                if (field.field_type === 'dropdown' && field.options) {
                  try { const p = JSON.parse(field.options); if (Array.isArray(p)) opts = p; } catch { /* ignore */ }
                }
                return (
                  <div key={field.id}>
                    <label className="block text-[14px] font-semibold text-[#111827] mb-2">
                      {(field.field_label === 'Projektname' || field.field_label === 'KI-Anfrage-Name') ? t('createProjectModal.projectNameLabel') : field.field_label}
                      {field.required && <span className="text-[#EF4444] ml-1">*</span>}
                    </label>
                    {field.field_type === 'textarea' ? (
                      <textarea
                        value={value}
                        onChange={(e) => set(e.target.value)}
                        rows={3}
                        className={baseInputCls + ' resize-none'}
                        onFocus={(e) => e.target.style.borderColor = aispaceColor}
                        onBlur={(e) => e.target.style.borderColor = error ? '#EF4444' : '#E5E7EB'}
                      />
                    ) : field.field_type === 'dropdown' ? (
                      <select
                        value={value}
                        onChange={(e) => set(e.target.value)}
                        className={baseInputCls + ' bg-white'}
                        onFocus={(e) => e.target.style.borderColor = aispaceColor}
                        onBlur={(e) => e.target.style.borderColor = error ? '#EF4444' : '#E5E7EB'}
                      >
                        <option value="">—</option>
                        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input
                        type={field.field_type === 'number' ? 'number' : (field.field_type === 'date' ? 'date' : 'text')}
                        value={value}
                        onChange={(e) => set(e.target.value)}
                        className={baseInputCls}
                        onFocus={(e) => e.target.style.borderColor = aispaceColor}
                        onBlur={(e) => e.target.style.borderColor = error ? '#EF4444' : '#E5E7EB'}
                      />
                    )}
                    {field.helper_text && !error && (
                      <p className="text-[12px] text-[#6B7280] mt-1">{field.helper_text}</p>
                    )}
                    {error && (
                      <p className="text-[12px] text-[#EF4444] mt-1">{error}</p>
                    )}
                  </div>
                );
              })}

              {/* Upload sections — hidden when the selected agent has its own
                  Project Setup custom fields. */}
              {!customFieldsLoading && customFields.length === 0 && (
              <>
              {/* Upload Method */}
              <div>
                <label className="block text-[14px] font-semibold text-[#111827] mb-3">{t('createProjectModal.uploadMethodLabel')}</label>
                <div className="space-y-3">
                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={documentInputRef}
                    onChange={handleDocumentUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png"
                    multiple
                    className="hidden"
                  />

                  {/* Upload from device */}
                  <button
                    onClick={() => {
                      setNewProject(prev => ({ ...prev, uploadMethod: 'device' }));
                      documentInputRef.current?.click();
                    }}
                    className="w-full flex items-center gap-4 p-4 rounded-xl transition-colors"
                    style={{
                      border: newProject.uploadMethod === 'device' ? `1.5px solid ${aispaceColor}` : '0.5px solid #E5E7EB',
                      backgroundColor: newProject.uploadMethod === 'device' ? `${aispaceColor}15` : 'transparent'
                    }}
                  >
                    <div className="w-10 h-10 flex items-center justify-center">
                      <Upload size={22} className="text-[#6B7280]" />
                    </div>
                    <div className="text-left">
                      <p className="text-[15px] font-semibold text-[#111827]">{t('createProjectModal.uploadFromDevice')}</p>
                      <p className="text-[13px] text-[#6B7280]">{t('createProjectModal.uploadFromDeviceHint')}</p>
                    </div>
                  </button>

                  {/* Connect SharePoint */}
                  <button
                    onClick={() => {
                      if (!newProject.agent) {
                        toast.error(t('toast.selectAgentFirst'));
                        return;
                      }
                      setNewProject(prev => ({ ...prev, uploadMethod: 'sharepoint' }));
                      setShowSharePointBrowser(true);
                    }}
                    className="w-full flex items-center gap-4 p-4 rounded-xl transition-colors"
                    style={{
                      border: newProject.uploadMethod === 'sharepoint' ? `1.5px solid ${aispaceColor}` : '0.5px solid #E5E7EB',
                      backgroundColor: newProject.uploadMethod === 'sharepoint' ? `${aispaceColor}15` : 'transparent'
                    }}
                  >
                    <div className="w-10 h-10 flex items-center justify-center">
                      <FolderOpen size={22} className="text-[#6B7280]" />
                    </div>
                    <div className="text-left">
                      <p className="text-[15px] font-semibold text-[#111827]">{t('createProjectModal.connectSharePoint')}</p>
                      <p className="text-[13px] text-[#6B7280]">{t('createProjectModal.connectSharePointHint')}</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Uploaded Files List - shown when files are uploaded */}
              {newProject.uploadedFiles.length > 0 && (
                <div className="space-y-2">
                  {newProject.uploadedFiles.map(file => (
                    <div key={file.id} className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-lg">
                      <div className="flex items-center gap-3">
                        {file.type?.startsWith('image/')
                          ? <ImageIcon size={20} className="text-[#6B7280]" />
                          : <FileText size={20} className="text-[#6B7280]" />}
                        <div>
                          <p className="text-[14px] font-medium text-[#374151] truncate max-w-[280px]">{file.name}</p>
                          <p className="text-[12px] text-[#9CA3AF]">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeUploadedFile(file.id)}
                        className="p-1 hover:bg-[#FEE2E2] rounded transition-colors"
                      >
                        <X size={16} className="text-[#EF4444]" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* SharePoint imported files list */}
              {newProject.uploadMethod === 'sharepoint' && importedSharePointFiles.length > 0 && (
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                      </svg>
                      <span className="text-[14px] font-semibold text-[#111827]">SharePoint-Dateien ({importedSharePointFiles.filter(f => f.status === 'synced').length})</span>
                    </div>
                    <button
                      onClick={() => setShowSharePointBrowser(true)}
                      className="text-[13px] font-medium hover:opacity-80"
                      style={{ color: aispaceColor }}
                    >
                      + Weitere Dateien hinzufügen
                    </button>
                  </div>

                  {/* Successfully imported files */}
                  {importedSharePointFiles.filter(f => f.status === 'synced').length > 0 && (
                    <div className="space-y-2 max-h-[150px] overflow-y-auto thin-scrollbar">
                      {importedSharePointFiles
                        .filter(f => f.status === 'synced')
                        .map(file => (
                          <div key={file.id} className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                              <FileText size={16} className="text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[14px] font-medium text-[#374151] truncate">{file.name}</p>
                              <p className="text-[12px] text-green-600">{file.pageCount} Seiten • Aus SharePoint importiert</p>
                            </div>
                            <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Failed files with error */}
                  {importedSharePointFiles.filter(f => f.status === 'error').length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[12px] font-medium text-red-600">Import fehlgeschlagen:</p>
                      {importedSharePointFiles
                        .filter(f => f.status === 'error')
                        .map(file => (
                          <div key={file.id} className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                              <AlertCircle size={16} className="text-red-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[14px] font-medium text-[#374151] truncate">{file.name}</p>
                              <p className="text-[12px] text-red-600 truncate">{file.error}</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {/* SharePoint configured but no files yet */}
              {newProject.uploadMethod === 'sharepoint' && newProject.sharePointConfigured && importedSharePointFiles.length === 0 && (
                <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-[14px] text-blue-700">SharePoint verbunden - wählen Sie Dateien zum Importieren aus</span>
                  </div>
                  <button
                    onClick={() => setShowSharePointBrowser(true)}
                    className="text-[13px] text-blue-700 hover:text-blue-800 underline"
                  >
                    Dateien durchsuchen
                  </button>
                </div>
              )}
              </>
              )}
              </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] rounded-b-xl">
              <button
                onClick={closeCreateProjectModal}
                disabled={isCreatingProject}
                className="px-6 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleAnalyze}
                disabled={!newProject.agent || (customFields.length === 0 && !newProject.projectName) || isCreatingProject}
                className="px-6 py-2.5 text-[14px] font-medium text-white rounded-lg transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: aispaceColor }}
              >
                {isCreatingProject ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('createProjectModal.creating')}
                  </>
                ) : (
                  t('createProjectModal.analyze')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint File Browser - wrapped with higher z-index to appear on top of Create Project modal */}
      {showSharePointBrowser && (
        <div className="fixed inset-0 z-[300]">
          <SharePointFileBrowser
            isOpen={showSharePointBrowser}
            onClose={handleSharePointBrowserClose}
            organizationId={userData?.organization_id}
            taskId={newProject.agent}
            userOrganizationId={userData?.organization_id}
          />
        </div>
      )}
      {/* Project Filter Modal */}
      {projFilterOpen && (() => {
        const agents = tasks || [];
        const months = [
          { v: '0', l: t('months.january') }, { v: '1', l: t('months.february') }, { v: '2', l: t('months.march') }, { v: '3', l: t('months.april') },
          { v: '4', l: t('months.may') }, { v: '5', l: t('months.june') }, { v: '6', l: t('months.july') }, { v: '7', l: t('months.august') },
          { v: '8', l: t('months.september') }, { v: '9', l: t('months.october') }, { v: '10', l: t('months.november') }, { v: '11', l: t('months.december') },
        ];
        const sortOpts = [{ v: 'newest', l: t('filterModal.newestFirst') }, { v: 'oldest', l: t('filterModal.oldestFirst') }];
        const selStyle = (active) => active
          ? { backgroundColor: `${aispaceColor}1A`, borderColor: aispaceColor, color: aispaceColor }
          : { borderColor: '#E5E7EB', color: '#111827', backgroundColor: 'white' };

        // Question count for an agent: derived from any project that uses that
        // agent (the SQ data is fetched per project on mount). Falls back to 0
        // if no project for that agent has loaded its questions yet.
        const getAgentQuestionCount = (agentId) => {
          if (!agentId) return 0;
          for (const item of chatHistory) {
            if (item.task_id?.toString() !== agentId.toString()) continue;
            const data = historyData.get(item.id) || {};
            if (data.questions?.length) return data.questions.length;
          }
          return 0;
        };
        const noAgentSelected = draftAgents.length === 0;
        const maxAgentQuestions = draftAgents.reduce(
          (m, a) => Math.max(m, getAgentQuestionCount(a)),
          0
        );
        const lowMax = Math.floor(maxAgentQuestions / 3);
        const medMax = Math.floor((2 * maxAgentQuestions) / 3);
        const progressOpts = [
          { v: 'low',    l: t('filterModal.low'),    range: maxAgentQuestions > 0 ? ` (0-${lowMax})` : '' },
          { v: 'medium', l: t('filterModal.medium'), range: maxAgentQuestions > 0 ? ` (${lowMax + 1}-${medMax})` : '' },
          { v: 'high',   l: t('filterModal.high'),   range: maxAgentQuestions > 0 ? ` (${medMax + 1}-${maxAgentQuestions})` : '' },
        ];
        const effectiveDraftProgress = noAgentSelected ? null : draftProgress;

        return (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[140]" onClick={() => setProjFilterOpen(false)}>
            <div className="bg-white rounded-2xl w-[600px] max-h-[90vh] shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB] rounded-t-2xl">
                <h2 className="text-[20px] font-bold text-[#111827]">{t('filterModal.title')}</h2>
                <button onClick={() => setProjFilterOpen(false)} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors">
                  <X size={20} className="text-[#6B7280]" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-6 overflow-y-auto flex-1">
                {/* Agents multi-select */}
                <div>
                  <p className="text-[14px] font-semibold text-[#111827] mb-2">{t('filterModal.agentsLabel')}</p>
                  <div className="relative" ref={agentDdRef}>
                    <button type="button" onClick={() => { setAgentDdOpen(v => !v); setMonthDdOpen(false); setSortDdOpen(false) }}
                      className="w-full flex items-center justify-between border border-[#E5E7EB] rounded-xl px-4 py-3 bg-white hover:bg-[#F9FAFB] focus:outline-none min-h-[48px] flex-wrap gap-1"
                      style={agentDdOpen ? { borderColor: aispaceColor } : {}}
                    >
                      <div className="flex flex-wrap gap-1 flex-1">
                        {draftAgents.length === 0 && <span className="text-[14px] text-[#9CA3AF]">{t('filterModal.agentsPlaceholder')}</span>}
                        {draftAgents.map(id => {
                          const a = agentsForModal.find(x => x.id.toString() === id);
                          return a ? (
                            <span key={id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-medium bg-[#F3F4F6] text-[#111827]">
                              {a.name}
                              <button type="button" onClick={(e) => { e.stopPropagation(); setDraftAgents(p => p.filter(x => x !== id)) }}
                                className="hover:text-red-500"
                              ><X size={12} /></button>
                            </span>
                          ) : null;
                        })}
                      </div>
                      <ChevronDown size={16} className={`text-[#6B7280] flex-shrink-0 transition-transform ${agentDdOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {agentDdOpen && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-30 max-h-[200px] overflow-y-auto py-1">
                        {agentsForModal.map(a => {
                          const sel = draftAgents.includes(a.id.toString());
                          return (
                            <button key={a.id} type="button"
                              onClick={() => setDraftAgents(p => sel ? p.filter(x => x !== a.id.toString()) : [...p, a.id.toString()])}
                              className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] ${sel ? 'bg-[#F3F4F6]' : ''}`}
                            >
                              <span className="truncate">{a.name}</span>
                              {sel && <Check size={16} style={{ color: aispaceColor }} />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Creation Date */}
                <div>
                  <p className="text-[14px] font-semibold text-[#111827] mb-2">{t('filterModal.creationDateLabel')}</p>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    {[{ v: 'today', l: t('filterModal.today') }, { v: '7days', l: t('filterModal.last7days') }, { v: '30days', l: t('filterModal.last30days') }].map(o => (
                      <button key={o.v} type="button"
                        onClick={() => setDraftDate(draftDate === o.v ? null : o.v)}
                        className="px-4 py-3 rounded-xl border text-[14px] font-semibold transition-colors"
                        style={selStyle(draftDate === o.v)}
                      >{o.l}</button>
                    ))}
                  </div>
                  {/* Month dropdown */}
                  <div className="relative" ref={monthDdRef}>
                    <button type="button" onClick={() => { setMonthDdOpen(v => !v); setAgentDdOpen(false); setSortDdOpen(false) }}
                      className="w-full flex items-center justify-between border border-[#E5E7EB] rounded-xl px-4 py-3 bg-white hover:bg-[#F9FAFB] focus:outline-none text-[14px]"
                      style={monthDdOpen ? { borderColor: aispaceColor } : {}}
                    >
                      <span className={draftMonth ? 'text-[#111827]' : 'text-[#9CA3AF]'}>
                        {draftMonth ? months.find(m => m.v === draftMonth)?.l : t('filterModal.monthPlaceholder')}
                      </span>
                      <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${monthDdOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {monthDdOpen && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-30 max-h-[200px] overflow-y-auto py-1">
                        {months.map(m => (
                          <button key={m.v} type="button"
                            onClick={() => { setDraftMonth(draftMonth === m.v ? '' : m.v); setMonthDdOpen(false) }}
                            className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] ${draftMonth === m.v ? 'bg-[#F3F4F6]' : ''}`}
                          >
                            <span>{m.l}</span>
                            {draftMonth === m.v && <Check size={16} style={{ color: aispaceColor }} />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Progress of Q&A */}
                <div>
                  <p className="text-[14px] font-semibold text-[#111827] mb-2">{t('filterModal.progressLabel')}</p>
                  <div className="grid grid-cols-3 gap-3">
                    {progressOpts.map(o => (
                      <button key={o.v} type="button"
                        disabled={noAgentSelected}
                        onClick={() => { if (!noAgentSelected) setDraftProgress(draftProgress === o.v ? null : o.v); }}
                        className="px-4 py-3 rounded-xl border text-[14px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={selStyle(effectiveDraftProgress === o.v)}
                      >{o.l}{o.range}</button>
                    ))}
                  </div>
                </div>

                {/* Sort By */}
                <div>
                  <p className="text-[14px] font-semibold text-[#111827] mb-2">{t('filterModal.sortByLabel')}</p>
                  <div className="relative" ref={sortDdRef}>
                    <button type="button" onClick={() => { setSortDdOpen(v => !v); setAgentDdOpen(false); setMonthDdOpen(false) }}
                      className="w-full flex items-center justify-between border border-[#E5E7EB] rounded-xl px-4 py-3 bg-white hover:bg-[#F9FAFB] focus:outline-none text-[14px]"
                      style={sortDdOpen ? { borderColor: aispaceColor } : {}}
                    >
                      <span className="text-[#111827]">{sortOpts.find(s => s.v === draftSort)?.l}</span>
                      <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${sortDdOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {sortDdOpen && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-30 py-1">
                        {sortOpts.map(s => (
                          <button key={s.v} type="button"
                            onClick={() => { setDraftSort(s.v); setSortDdOpen(false) }}
                            className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] ${draftSort === s.v ? 'bg-[#F3F4F6]' : ''}`}
                          >
                            <span>{s.l}</span>
                            {draftSort === s.v && <Check size={16} style={{ color: aispaceColor }} />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] rounded-b-2xl">
                <button onClick={() => {
                  setDraftAgents([]); setDraftDate(null); setDraftMonth(''); setDraftProgress(null); setDraftSort('newest');
                  setProjFilterAgents([]); setProjFilterDate(null); setProjFilterMonth(''); setProjFilterProgress(null); setProjFilterSort('newest');
                  setCurrentPage(1); setProjFilterOpen(false);
                }} className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors">
                  {t('filterModal.resetAll')}
                </button>
                <button onClick={() => {
                  setProjFilterAgents(draftAgents); setProjFilterDate(draftDate); setProjFilterMonth(draftMonth);
                  setProjFilterProgress(draftProgress); setProjFilterSort(draftSort);
                  setCurrentPage(1); setProjFilterOpen(false);
                }} className="px-6 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors"
                  style={{ backgroundColor: aispaceColor }}
                >
                  {t('filterModal.apply')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      <ShareModal
        isOpen={!!shareTarget}
        onClose={() => { setShareTarget(null); fetchMyShares(); }}
        onInviteSent={fetchMyShares}
        historyId={shareTarget?.id || ''}
        projectName={shareTarget?.name || ''}
        currentUser={{
          name: userData?.user_fullname || userData?.user_email || localStorage.getItem('email'),
          email: userData?.user_email || localStorage.getItem('email'),
        }}
      />
    </div>
  );
};

export default History;
